import { useCallback, useMemo, useRef, useState } from "react";
import {
  buildConsolidatedHotelFilterCards,
  countLastResultsSummaryRows,
  countSummaryRowsForHotel,
  fingerprintLastResultsSummary,
  resolveAvailableHotelFilters,
} from "../utils/customerChatHotelFilter";

/**
 * @param {{ cvacServerStateRef: React.MutableRefObject<unknown>, messages: unknown[] }} args
 */
export function useCustomerVacationChatHotelFilter({ cvacServerStateRef, messages }) {
  const [selectedHotelFilter, setSelectedHotelFilter] = useState(null);
  const summaryFingerprintRef = useRef("");
  const [serverStateTick, setServerStateTick] = useState(0);

  const resetHotelFilter = useCallback(() => {
    summaryFingerprintRef.current = "";
    setSelectedHotelFilter(null);
  }, []);

  /**
   * Call after `captureCvacServerStateFromPayload` on each successful turn envelope.
   * @param {unknown} envelope
   */
  const onServerStateCaptured = useCallback((envelope) => {
    const st =
      envelope && typeof envelope === "object"
        ? envelope.state ?? envelope.payload?.state
        : null;
    const fp = fingerprintLastResultsSummary(st);
    if (fp !== summaryFingerprintRef.current) {
      summaryFingerprintRef.current = fp;
      setSelectedHotelFilter(null);
    }
    setServerStateTick((n) => n + 1);
  }, []);

  const availableHotelFilters = useMemo(() => {
    const st = cvacServerStateRef?.current;
    return resolveAvailableHotelFilters(st, messages);
  }, [cvacServerStateRef, messages, serverStateTick]);

  /** Latest assistant message that still carries hotel cards (search / load-more). Not the first in thread — avoids anchoring on an older result set. */
  const hotelFilterAnchorMessageIndex = useMemo(() => {
    if (!Array.isArray(messages)) return -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (
        m &&
        m.role === "assistant" &&
        Array.isArray(m.cards) &&
        m.cards.length > 0
      ) {
        return i;
      }
    }
    return -1;
  }, [messages]);

  const latestSearchResultsMeta = useMemo(() => {
    if (!Array.isArray(messages)) return null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.role === "assistant" && m.search_results_meta) return m.search_results_meta;
    }
    return null;
  }, [messages]);

  const snapshotTotalCount = useMemo(() => {
    return countLastResultsSummaryRows(cvacServerStateRef?.current);
  }, [cvacServerStateRef, serverStateTick]);

  const filteredSnapshotCount = useMemo(
    () => countSummaryRowsForHotel(cvacServerStateRef?.current, selectedHotelFilter),
    [cvacServerStateRef, serverStateTick, selectedHotelFilter]
  );

  /** Full-snapshot filter list; `null` = no `last_results_summary` rows for selection (use per-message cards). */
  const consolidatedHotelFilterCards = useMemo(() => {
    if (!selectedHotelFilter) return null;
    return buildConsolidatedHotelFilterCards(
      cvacServerStateRef?.current,
      messages,
      selectedHotelFilter
    );
  }, [cvacServerStateRef, messages, serverStateTick, selectedHotelFilter]);

  const statusLine = useMemo(() => {
    if (selectedHotelFilter) {
      const n = filteredSnapshotCount;
      return `נמצאו ${n} תוצאות עבור המלון שנבחר`;
    }
    if (snapshotTotalCount > 0) {
      return `נמצאו ${snapshotTotalCount} תוצאות בחיפוש הנוכחי`;
    }
    const tr = latestSearchResultsMeta?.total_results ?? latestSearchResultsMeta?.chat_unique_results_count;
    if (typeof tr === "number" && tr > 0) {
      return `נמצאו ${tr} תוצאות בחיפוש הנוכחי`;
    }
    return null;
  }, [
    selectedHotelFilter,
    filteredSnapshotCount,
    snapshotTotalCount,
    latestSearchResultsMeta,
  ]);

  const showHotelFilterBar =
    hotelFilterAnchorMessageIndex >= 0 && availableHotelFilters.length > 0;

  return {
    selectedHotelFilter,
    setSelectedHotelFilter,
    availableHotelFilters,
    hotelFilterAnchorMessageIndex,
    showHotelFilterBar,
    statusLine,
    consolidatedHotelFilterCards,
    onServerStateCaptured,
    resetHotelFilter,
  };
}
